import { useCallback, useState, useEffect } from "react";
import {
  Stepper,
  Button,
  Group,
  TextInput,
  Select,
  Radio,
  Checkbox,
  Alert,
  Text,
  Modal,
  Card,
  Badge,
  Stack,
  Textarea,
  Loader,
  ActionIcon,
  CopyButton,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconAlertCircle,
  IconPlane,
} from "@tabler/icons-react";
// import PhoneInput from "react-phone-input-2";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
// import "react-phone-input-2/lib/style.css";
import {
  requestSingleFlight, // Using EXISTING function (now enhanced)
  sentVerifyNumberRequest,
  verifyReceivedSMSCode,
  getPPRPricingByTypeUnregistered, // Updated to use new database-driven function
  getAllPPRPricingUnregistered, // NEW: Get all pricing options
  getFlightPurposes, // NEW function
} from "../misc/api";
import { validateEmail } from "../misc/functions";
import { QuestionModal } from "../Components/QuestionModal";
import { AirplaneRules } from "../Components/AirplaneRules";

export function RegisterSingleScreen() {
  // Enhanced state management
  const [request, setRequest] = useState({
    // Personal Information
    full_name: "",
    email: "",
    phone: "",

    // Flight Information
    aircraft_registration: "",
    aircraft_type: "",
    weight_category: "",
    flight_type: "single_day",
    selected_pricing_id: null,
    purpose_of_flight: "PRIVATE",
    flight_date: null,
    departure_place: "",
    arrival_place: "",

    // Enhanced workflow fields
    ppr_type: "one_time",
    payment_method: "online",
    rules_acknowledged: false,
    quiz_completed: false,
    quiz_score: 0,
    phone_verified: false,
  });

  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ruleTestModal, setRuleTestModal] = useState(false);

  // Enhanced workflow states
  const [flightPurposes, setFlightPurposes] = useState([]);
  const [flightTypeOptions, setFlightTypeOptions] = useState([]);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  // const [rulesLanguage, setRulesLanguage] = useState("en");
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);

  // SMS verification
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  // Final result
  const [pprResult, setPprResult] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load flight type options from backend
      const pricingResponse = await getAllPPRPricingUnregistered();
      const allPricing = pricingResponse?.data || [];

      // Filter for one_time PPR types only (for single flights)
      const oneTimePricing = allPricing.filter(p => p.ppr_type === 'one_time' && p.is_active);
      setFlightTypeOptions(oneTimePricing);

      // const purposesResponse = await getFlightPurposes();
      // setFlightPurposes(purposesResponse?.data || []);
    } catch (error) {
      console.error("Error loading initial data:", error);
      setError("Failed to load form data. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateRequest = useCallback((field, value) => {
    setRequest((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  }, []);

  // Step validation
  const validateStep1 = () => {
    const required = [
      "full_name",
      "email",
      "phone",
      "aircraft_registration",
      "aircraft_type",
      "weight_category",
    ];

    for (const field of required) {
      if (!request[field]) {
        setError(`${field.replace("_", " ")} is required`);
        return false;
      }
    }

    if (!validateEmail(request.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!request.selected_pricing_id) {
      setError("Please select a flight type");
      return false;
    }
    const required = ["flight_type", "purpose_of_flight", "flight_date"];
    for (const field of required) {
      if (!request[field]) {
        setError(`${field.replace("_", " ")} is required`);
        return false;
      }
    }
    return true;
  };

  const validateStep3 = () => {
    if (!request.phone_verified) {
      setError("Please verify your phone number");
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!request.rules_acknowledged) {
      setError("Please read and acknowledge the airport rules");
      return false;
    }
    return true;
  };

  const validateStep5 = () => {
    return true;
  };

  // SMS verification functions
  const sendVerificationCode = async () => {
    try {
      setLoading(true);
      await sentVerifyNumberRequest(request.phone);
      setCodeSent(true);
      setError(null);
    } catch (error) {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyPhone = async () => {
    try {
      setLoading(true);
      await verifyReceivedSMSCode(request.phone, verificationCode);
      updateRequest("phone_verified", true);
      setError(null);
    } catch (error) {
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Rules acknowledgment
  const acknowledgeRules = (e) => {
    updateRequest("rules_acknowledged", e);
  };

  // Quiz functions
  const startQuiz = () => {
    setRuleTestModal(true);
  };

  const submitQuiz = () => {
    let correct = 0;
    currentQuestions.forEach((q) => {
      if (userAnswers[q.id] === q.correct_answer) {
        correct++;
      }
    });

    const score = correct / currentQuestions.length;
    updateRequest("quiz_score", score);
    updateRequest("quiz_completed", true);

    if (score < 0.8) {
      setError(
        `Quiz score: ${Math.round(
          score * 100
        )}%. You need at least 80% to continue.`
      );
    }
  };

  // Pricing is now set directly when user selects a flight type radio option
  // No separate API call needed

  const submitRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      // Ensure pricing is loaded before submitting
      if (!pricing) {
        setError("Pricing information not loaded. Please try again or contact support.");
        setLoading(false);
        return;
      }

      const requestData = {
        ...request,
        pricing_id: pricing.pricing_id,
        pricing_amount: pricing.price,
        currency: pricing.currency,
        requires_payment: pricing.requires_payment,
        flight_date: request.flight_date?.toISOString?.() || request.flight_date,
        customer_ip: window.location.hostname, // For EveryPay fraud detection
      };

      // Call API using helper function (no direct fetch!)
      const response = await requestSingleFlight(requestData);

      // Handle error response
      if (response.error) {
        setError(response.error || "Failed to submit PPR request. Please try again.");
        console.error("PPR request error:", response.error);
        return;
      }

      // Extract response data
      const resultData = response.data || response;

      // Check if online payment is required
      // if (resultData.payment_required &&
      //   request.payment_method === "online" &&
      //   resultData.payment_link) {

      if (request.payment_method === "online" &&
        resultData.payment_link) {

        // Store order reference for when user returns
        localStorage.setItem("pending_order", resultData.order_reference);
        localStorage.setItem("pending_ppr", resultData.ppr_number);

        console.log("Redirecting to EveryPay:", resultData.payment_link);

        // Redirect to EveryPay payment page
        window.location.href = resultData.payment_link;

        // Don't proceed with success display - user is being redirected
        return;
      }

      // No payment needed or pay at airport - show success
      setPprResult(resultData);
      setShowSuccess(true);
      setActive(6); // Move to final step

    } catch (error) {
      setError("Failed to submit PPR request. Please try again.");
      console.error("PPR request error:", error);
    } finally {
      setLoading(false);
    }
  };

  // const submitRequest = async () => {
  //   try {
  //     setLoading(true);

  //     const requestData = {
  //       ...request,
  //       pricing_amount: pricing?.price || 0,
  //       currency: pricing?.currency || "EUR",
  //       requires_payment: pricing?.requires_payment !== false,
  //       flight_date:
  //         request.flight_date?.toISOString?.() || request.flight_date,
  //     };

  //     const response = await requestSingleFlight(requestData);

  //     if (response.error) {
  //       setError("Failed to submit PPR request. Please try again.");
  //       console.error("PPR request error:", response.error);
  //       return;
  //     }

  //     // Handle both response.data and direct response
  //     const resultData = response.data || response;
  //     setPprResult(resultData);
  //     setShowSuccess(true);
  //     setActive(6); // Final step
  //   } catch (error) {
  //     setError("Failed to submit PPR request. Please try again.");
  //     console.error("PPR request error:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Navigation
  const nextStep = async () => {
    let isValid = false;

    switch (active) {
      case 0:
        isValid = validateStep1();
        break;
      case 1:
        isValid = validateStep2();
        break;
      case 2:
        isValid = validateStep3();
        break;
      case 3:
        isValid = validateStep4();
        break;
      case 4:
        isValid = validateStep5();
        break;
      case 5:
        await submitRequest();
        return;
      default:
        isValid = true;
    }

    if (isValid) {
      setActive((prev) => prev + 1);
      setError(null);
    }
  };

  const prevStep = () => {
    setActive((prev) => Math.max(0, prev - 1));
    setError(null);
  };

  // Render steps (using your existing UI patterns)
  const renderStep1 = () => (
    <Stack spacing="md">
      <Text size="lg" weight={500}>
        Personal & Aircraft Information
      </Text>

      <TextInput
        label="Full Name"
        placeholder="Enter your full name"
        value={request.full_name}
        onChange={(e) => updateRequest("full_name", e.target.value)}
        required
      />

      <TextInput
        label="Email Address"
        placeholder="your.email@example.com"
        value={request.email}
        onChange={(e) => updateRequest("email", e.target.value)}
        type="email"
        required
      />

      <div>
        <Text size="sm" mb={5}>
          Phone Number *
        </Text>
        {/* <PhoneInput
          country="lv"
          value={request.phone}
          onChange={(phone) => updateRequest("phone", phone)}
          inputStyle={{ width: "100%" }}
          autoComplete={false}
          inputProps={{
            autoComplete: "off",
          }}
        /> */}
        <PhoneInput
          defaultCountry="LV"
          value={request.phone}
          onChange={(phone) => updateRequest("phone", phone)}
        />
        {console.log(request.phone)}
      </div>

      <TextInput
        label="Aircraft Registration"
        placeholder="e.g., YL-ABC"
        value={request.aircraft_registration}
        onChange={(e) =>
          updateRequest("aircraft_registration", e.target.value.toUpperCase())
        }
        required
      />

      <TextInput
        label="Aircraft Type"
        placeholder="e.g., Cessna 172"
        value={request.aircraft_type}
        onChange={(e) => updateRequest("aircraft_type", e.target.value)}
        required
      />

      <Select
        label="Aircraft Weight (MTOM)"
        placeholder="Select weight category"
        value={request.weight_category}
        onChange={(value) => updateRequest("weight_category", value)}
        data={[
          { value: "up_to_600", label: "Up to 600 kg" },
          { value: "601_to_2400", label: "601 – 2400 kg" },
          { value: "above_2400", label: "More than 2400 kg" },
        ]}
        required
      />
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing="md">
      <Text size="lg" weight={500}>
        Flight Details
      </Text>

      <Radio.Group
        label="Flight Type"
        value={request.selected_pricing_id ? String(request.selected_pricing_id) : ""}
        onChange={(value) => {
          const selected = flightTypeOptions.find(o => String(o.pricing_id) === value);
          if (selected) {
            updateRequest("selected_pricing_id", selected.pricing_id);
            updateRequest("flight_type", selected.flight_type);
            setPricing({
              pricing_id: selected.pricing_id,
              pricing_code: selected.pricing_code,
              product: selected.ppr_type,
              flight_type: selected.flight_type,
              price: parseFloat(selected.price),
              currency: selected.currency || 'EUR',
              requires_payment: selected.requires_payment,
              description: selected.description,
            });
            setPricingError(null);
          }
        }}
        required
      >
        <Stack spacing="xs">
          {flightTypeOptions.length > 0 ? (
            flightTypeOptions.map((option) => (
              <Radio
                key={option.pricing_id}
                value={String(option.pricing_id)}
                label={
                  <Group position="apart" style={{ width: '100%' }}>
                    <div>
                      <Text weight={500}>{option.pricing_name}</Text>
                      {option.description && (
                        <Text size="xs" color="dimmed">{option.description}</Text>
                      )}
                    </div>
                    <Badge color={option.price === 0 ? 'green' : 'blue'}>
                      {option.price === 0 ? 'FREE' : `€${option.price}`}
                    </Badge>
                  </Group>
                }
              />
            ))
          ) : (
            <Alert icon={<IconAlertCircle />} color="yellow">
              Loading flight type options...
            </Alert>
          )}
        </Stack>
      </Radio.Group>

      {flightPurposes.length > 0 && (
        <Select
          label="Purpose of Flight"
          value={request.purpose_of_flight}
          onChange={(value) => updateRequest("purpose_of_flight", value)}
          data={flightPurposes.map((p) => ({
            value: p.purpose_code,
            label: p.purpose_name,
          }))}
          required
        />
      )}

      <DateInput
        label="Flight Date"
        placeholder="Select flight date"
        value={request.flight_date}
        onChange={(date) => updateRequest("flight_date", date)}
        minDate={new Date()}
        maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
        required
      />

      <Group grow>
        <TextInput
          label="Departure Airport"
          placeholder="ICAO code (e.g., EVRS)"
          value={request.departure_place}
          onChange={(e) =>
            updateRequest("departure_place", e.target.value.toUpperCase())
          }
        />
        <TextInput
          label="Arrival Airport"
          placeholder="ICAO code (e.g., EVRS)"
          value={request.arrival_place}
          onChange={(e) =>
            updateRequest("arrival_place", e.target.value.toUpperCase())
          }
        />
      </Group>

      {/* Pricing Preview */}
      {pricing && (
        <Alert
          icon={<IconInfoCircle />}
          color={pricing.price === 0 ? "green" : "blue"}
        >
          <Group position="apart">
            <div>
              <Text size="sm" weight={500}>
                Estimated Cost:
              </Text>
              {pricing.description && (
                <Text size="xs" color="dimmed">
                  {pricing.description}
                </Text>
              )}
            </div>
            <Badge
              size="lg"
              color={pricing.price === 0 ? "green" : "blue"}
              variant="filled"
            >
              {pricing.price === 0 ? "FREE" : `EUR ${pricing.price}`}
            </Badge>
          </Group>
        </Alert>
      )}
    </Stack>
  );

  // SMS verification step
  const renderStep3 = () => (
    <Stack spacing="md">
      <Text size="lg" weight={500}>
        Phone Verification
      </Text>

      {!codeSent ? (
        <Stack spacing="md">
          <Text>We need to verify your phone number: {request.phone}</Text>
          <Button onClick={sendVerificationCode} loading={loading}>
            Send Verification Code
          </Button>
          {/* <Button
            style={{ backgroundColor: "green" }}
            onClick={() => {
              if (!request.phone) {
                return window.alert(
                  "Enter a phone number to receive questions"
                );
              }
              updateRequest("phone_verified", true);
            }}
          >
            Dev only - verify number - click here
          </Button> */}
        </Stack>
      ) : !request.phone_verified ? (
        <Stack spacing="md">
          <Text>Enter the verification code sent to {request.phone}</Text>
          <TextInput
            label="Verification Code"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            maxLength={6}
          />
          <Group>
            <Button onClick={verifyPhone} loading={loading}>
              Verify Phone
            </Button>
            <Button
              variant="subtle"
              onClick={sendVerificationCode}
              loading={loading}
            >
              Resend Code
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack spacing="md" align="center">
          <IconCheck size={48} color="green" />
          <Text color="green" weight={500}>
            Phone verified successfully!
          </Text>
        </Stack>
      )}
    </Stack>
  );

  const renderStep4 = () => (
    <Stack spacing="md">
      <Group position="apart">
        <AirplaneRules
          setAgreeChecked={acknowledgeRules}
          agreeChecked={request.rules_acknowledged}
        />
      </Group>
    </Stack>
  );

  // Quiz step
  const renderStep5 = () => (
    <Stack spacing="md">
      <Text size="lg" weight={500}>
        Knowledge Test
      </Text>

      {!request.quiz_completed ? (
        currentQuestions.length === 0 ? (
          <Stack spacing="md" align="center">
            <Text>
              Complete a knowledge test with 3 random questions (minimum 80% to
              pass)
            </Text>
            <Button onClick={startQuiz}>Start Knowledge Test</Button>
          </Stack>
        ) : (
          <Stack spacing="md">
            {currentQuestions.map((question, index) => (
              <Card key={question.id} withBorder>
                <Stack spacing="sm">
                  <Text weight={500}>{question.question_text}</Text>
                  <Radio.Group
                    value={userAnswers[question.id] || ""}
                    onChange={(value) =>
                      setUserAnswers((prev) => ({
                        ...prev,
                        [question.id]: value,
                      }))
                    }
                  >
                    <Stack spacing="xs">
                      {question.options?.map((option, optIndex) => (
                        <Radio key={optIndex} value={option} label={option} />
                      ))}
                    </Stack>
                  </Radio.Group>
                </Stack>
              </Card>
            ))}

            {Object.keys(userAnswers).length === currentQuestions.length && (
              <Button onClick={submitQuiz}>Submit Test</Button>
            )}
          </Stack>
        )
      ) : (
        <Stack spacing="md" align="center">
          <IconCheck
            size={48}
            color={request.quiz_score >= 0.8 ? "green" : "red"}
          />
          <Text
            color={request.quiz_score >= 0.8 ? "green" : "red"}
            weight={500}
          >
            Test Score: {Math.round(request.quiz_score * 100)}%
          </Text>
          {request.quiz_score < 0.8 && (
            <Button
              onClick={() => {
                updateRequest("quiz_completed", false);
                startQuiz();
              }}
            >
              Retake Test
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );

  // Payment step
  const renderStep6 = () => (
    <Stack spacing="md">
      <Text size="lg" weight={500}>
        Payment & Confirmation
      </Text>

      {pricing && (
        <Card withBorder>
          <Stack spacing="sm">
            <Group position="apart">
              <Text>Flight Type:</Text>
              <Text weight={500}>{request.flight_type.replace("_", " ")}</Text>
            </Group>
            <Group position="apart">
              <Text>Amount:</Text>
              <Badge
                size="lg"
                color={pricing.price === 0 ? "green" : "blue"}
                variant="filled"
              >
                {pricing.price === 0 ? "FREE" : `EUR ${pricing.price}`}
              </Badge>
            </Group>
            {pricing.description && (
              <Group position="apart">
                <Text>Description:</Text>
                <Text size="sm" color="dimmed">
                  {pricing.description}
                </Text>
              </Group>
            )}
          </Stack>
        </Card>
      )}

      {pricing?.price > 0 && (
        <Radio.Group
          label="Payment Method"
          value={request.payment_method}
          onChange={(value) => updateRequest("payment_method", value)}
        >
          <Stack spacing="xs">
            <Radio value="online" label="Pay Online Now" />
            <Radio value="airport" label="Pay at Airport" />
          </Stack>
        </Radio.Group>
      )}

      {/* <Button
        onClick={submitRequest}
        loading={loading}
        size="lg"
        leftIcon={<IconPlane />}
        disabled={pricingLoading}
      >
        Submit PPR Request
      </Button> */}
    </Stack>
  );

  // Success step
  const renderStep7 = () => (
    <Stack spacing="md" align="center">
      <IconCheck size={64} color="green" />
      <Text size="xl" weight={700} color="green">
        PPR Request Successful!
      </Text>
      {pprResult && (
        <Card withBorder style={{ width: "100%" }}>
          <Stack spacing="sm">
            <Group position="apart">
              <Text weight={500}>PPR Number:</Text>
              <Group spacing="xs">
                <Badge size="lg" variant="filled" color="green">
                  {pprResult?.ppr?.number}
                </Badge>
                <CopyButton value={pprResult?.ppr?.number}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied" : "Copy PPR Number"}>
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Group>

            <Group position="apart">
              <Text>Valid Until:</Text>
              <Text weight={500}>
                {pprResult.expires_at
                  ? new Date(pprResult.expires_at).toLocaleDateString()
                  : "30 days"}
              </Text>
            </Group>

            {pprResult.amount && (
              <Group position="apart">
                <Text>Amount:</Text>
                <Text
                  weight={500}
                  color={parseFloat(pprResult.amount) === 0 ? "green" : "blue"}
                >
                  {parseFloat(pprResult.amount) === 0
                    ? "FREE"
                    : `EUR ${pprResult.amount}`}
                </Text>
              </Group>
            )}

            {pprResult.payment_required &&
              request.payment_method === "airport" && (
                <Alert icon={<IconInfoCircle />} color="orange" size="sm">
                  <Text size="sm">
                    <strong>Payment Required:</strong> Please pay at the airport
                    before your flight.
                  </Text>
                </Alert>
              )}
          </Stack>
        </Card>
      )}

      <Alert icon={<IconInfoCircle />} color="blue">
        <Text size="sm">
          Your PPR number has been sent to your email address. Please include
          this number in your flight plan when submitting to ATC.
        </Text>
      </Alert>
    </Stack>
  );

  if (loading && active === 0) {
    return (
      <Stack align="center" spacing="md" style={{ padding: 40 }}>
        <Loader size="lg" />
        <Text>Loading PPR request form...</Text>
      </Stack>
    );
  }

  return (
    <Stack spacing="xl" style={{ padding: 20 }}>
      <QuestionModal
        userNr={request.phone}
        ruleTestModal={ruleTestModal}
        setRuleTestModal={setRuleTestModal}
        nextStep={nextStep}
      />
      <Stepper active={active} breakpoint="sm" allowNextStepsSelect={false}>
        <Stepper.Step
          label="Information"
          description="Personal & aircraft details"
        >
          {renderStep1()}
        </Stepper.Step>

        <Stepper.Step label="Flight Details" description="Flight information">
          {renderStep2()}
        </Stepper.Step>

        <Stepper.Step label="Verification" description="Phone verification">
          {renderStep3()}
        </Stepper.Step>

        <Stepper.Step label="Rules" description="Airport regulations">
          {renderStep4()}
        </Stepper.Step>

        <Stepper.Step label="Knowledge Test" description="Safety quiz">
          {renderStep5()}
        </Stepper.Step>

        <Stepper.Step label="Payment" description="Confirm & pay">
          {renderStep6()}
        </Stepper.Step>

        <Stepper.Completed>{renderStep7()}</Stepper.Completed>
      </Stepper>

      {error && (
        <Alert
          icon={<IconAlertCircle />}
          color="red"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {active < 6 && (
        <Group position="center" mt="xl">
          {active > 0 && (
            <Button variant="default" onClick={prevStep}>
              Back
            </Button>
          )}
          <Button
            onClick={nextStep}
            loading={loading}
            disabled={
              pricingLoading ||
              (active === 2 && !request.phone_verified) ||
              (active === 3 && !request.rules_acknowledged) ||
              (active === 4 &&
                (!request.quiz_completed || request.quiz_score < 0.8)) ||
              (active === 5 && !pricing)
            }
          >
            {active === 5 ? "Submit Request" : "Continue"}
          </Button>
        </Group>
      )}

      {/* Success Modal */}
      <Modal
        opened={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Flight Request Successful!"
        centered
        size="md"
      >
        {renderStep7()}

        <Group position="center" mt="lg">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Submit Another Request
          </Button>
          {/* <Button onClick={() => (window.location.href = "/register-options")}>
            Back to Options
          </Button> */}
        </Group>
      </Modal>
    </Stack>
  );
}